import {
  isDropDownNoSelection,
  OdinIcon,
  OdinIconSize,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useScreenSize,
  useTestTag,
} from '@myosh/odin-components';
import type {
  DropDownNoSelection,
  DropDownOptionTemplateProps,
  DropDownResult,
  JsonData,
} from '@myosh/odin-components';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewConfig } from '../../@types/views';
import { useAppDispatch } from '../../redux/hooks';
import type { DynamicPageFilters } from '../../redux/slices/dynamic-page-slice';
import {
  setSelectedAuthorFilter,
  setSelectedLastEditedFilter,
  setSelectedRecordsFilter,
} from '../../redux/slices/dynamic-page-slice';
import { dataTag } from '../../utils/data-tags';
import {
  defaultLastEditedFilterOption,
  defaultRecordAuthorFilterOption,
  defaultRecordFilterOption,
  lastEditedFilterOptions,
  recordAuthorFilterOptions,
  recordFilterOptions,
} from '../../utils/dynamic-page-utils';
import { forceAssert } from '../../utils/type-guards';
import OdinDropDown from '../fields/custom-drop-downs/odin-drop-down.component';

interface DynamicPageHeaderFiltersProps {
  isAllForms: boolean;
  viewConfigs: ViewConfig[] | undefined;
  selectedFilters: DynamicPageFilters;
  onViewConfigChange: (
    config?: DropDownResult | Array<DropDownResult> | DropDownNoSelection,
    updateView?: boolean
  ) => void;
}

export const DynamicPageHeaderFilters = ({
  isAllForms,
  viewConfigs,
  selectedFilters,
  onViewConfigChange,
}: DynamicPageHeaderFiltersProps) => {
  const { isMobile } = useScreenSize();

  const tagCreator = useTestTag('dynamic-page-header-filters');
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onLastEditedFilterChange = useCallback(
    debounce((value?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
      if (value && !isAllForms) {
        const selected = isDropDownNoSelection(value)
          ? defaultLastEditedFilterOption
          : Array.isArray(value)
            ? value[0]
            : value;
        dispatch(setSelectedLastEditedFilter(selected));
      }
    }, 5),
    [dispatch, isAllForms]
  );

  const onRecordsFilterChange = useCallback(
    debounce((value?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
      if (value) {
        const selected = isDropDownNoSelection(value)
          ? defaultRecordFilterOption
          : Array.isArray(value)
            ? value[0]
            : value;
        dispatch(setSelectedRecordsFilter(selected));
      }
    }, 5),
    [dispatch]
  );

  const onAuthorFilterChange = debounce((value?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
    if (value) {
      let selectedAuthorFilter: DropDownResult;

      if (isDropDownNoSelection(value)) {
        selectedAuthorFilter = defaultRecordAuthorFilterOption;
      } else {
        selectedAuthorFilter = !Array.isArray(value) ? value : value[0];
      }

      dispatch(setSelectedAuthorFilter(selectedAuthorFilter));
    }
  }, 5);

  const viewDropDownOptionTemplate = ({ text, onItemSelected, isSelected }: DropDownOptionTemplateProps) => (
    <p
      className={clsx(
        'odin-truncate odin-py-1 odin-px-2 odin-cursor-pointer hover:odin-bg-gray-4 hover:odin-text-mono-0',
        { 'odin-bg-gray-2 odin-text-mono-1': isSelected }
      )}
      onClick={onItemSelected}
      title={text}
      ref={tagCreator('view-dropdown-option-' + dataTag(text))}
    >
      {text}
    </p>
  );

  const dropdownClasses = clsx('w-full', { 'sm:!w-48': isAllForms, 'sm:!w-40': !isAllForms });

  const filterDropdowns = (
    <div
      className={clsx('flex gap-3', isMobile ? 'flex-col' : 'custom-scroll overflow-x-auto py-1')}
      ref={!isMobile ? tagCreator('action-dropdowns') : undefined}
    >
      <OdinDropDown
        textField="name"
        valueField="id"
        data={viewConfigs?.length ? forceAssert<JsonData>(viewConfigs) : []}
        allowSearch={false}
        allowClear={false}
        value={selectedFilters.viewConfig}
        className={dropdownClasses}
        label={t('view')}
        size="md"
        onChange={onViewConfigChange}
        optionTemplate={viewDropDownOptionTemplate}
        shouldUseCreatePortal
        optionListPanelShouldMatchTargetWidth
      />
      {!isAllForms && (
        <OdinDropDown
          textField="text"
          valueField="value"
          data={lastEditedFilterOptions}
          allowSearch={false}
          allowClear={false}
          setDefaultValue={false}
          className={dropdownClasses}
          label={t('last-edited')}
          size="md"
          onChange={onLastEditedFilterChange}
          value={selectedFilters.lastEdited}
          shouldUseCreatePortal
          optionListPanelShouldMatchTargetWidth
        />
      )}
      <OdinDropDown
        textField="text"
        valueField="value"
        data={recordFilterOptions}
        allowSearch={false}
        allowClear={false}
        setDefaultValue={false}
        className={dropdownClasses}
        label={t('show-records')}
        size="md"
        onChange={onRecordsFilterChange}
        value={selectedFilters.records}
        shouldUseCreatePortal
        optionListPanelShouldMatchTargetWidth
      />
      {!isAllForms && (
        <OdinDropDown
          textField="text"
          valueField="value"
          data={recordAuthorFilterOptions}
          allowSearch={false}
          allowClear={false}
          setDefaultValue={false}
          className={dropdownClasses}
          label={t('created-by')}
          size="md"
          onChange={onAuthorFilterChange}
          value={selectedFilters.author}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger>
          <OdinIcon icon="Filter" size={OdinIconSize.Small} />
        </PopoverTrigger>
        <PopoverContent>{filterDropdowns}</PopoverContent>
      </Popover>
    );
  }

  return filterDropdowns;
};
